import { cn } from '@/lib/utils'
import { Skeleton } from '../ui/skeleton'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * Animated loading spinner
 *
 * @example
 * <LoadingSpinner size="md" />
 */
export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-4',
    lg: 'h-12 w-12 border-4',
  }

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-primary border-t-transparent',
        sizeClasses[size],
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  )
}

interface LoadingOverlayProps {
  message?: string
  className?: string
}

/**
 * Full-screen or container loading overlay
 *
 * @example
 * <LoadingOverlay message="Loading data..." />
 */
export function LoadingOverlay({ message = 'Loading...', className }: LoadingOverlayProps) {
  return (
    <div
      className={cn(
        'flex min-h-[400px] w-full flex-col items-center justify-center gap-4',
        className
      )}
    >
      <LoadingSpinner size="lg" />
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
    </div>
  )
}

interface LoadingCardProps {
  lines?: number
  className?: string
}

/**
 * Skeleton loading card
 *
 * @example
 * <LoadingCard lines={3} />
 */
export function LoadingCard({ lines = 3, className }: LoadingCardProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-4 w-full', i === lines - 1 && 'w-3/4')}
        />
      ))}
    </div>
  )
}

/**
 * Table skeleton loader
 *
 * @example
 * <LoadingTable rows={5} columns={4} />
 */
export function LoadingTable({
  rows = 5,
  columns = 3,
  className,
}: {
  rows?: number
  columns?: number
  className?: string
}) {
  return (
    <div className={cn('space-y-3', className)}>
      {/* Header */}
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} className="h-8 flex-1" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-12 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Page skeleton loader with header and content
 *
 * @example
 * <LoadingPage />
 */
export function LoadingPage({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-4 p-4', className)}>
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>

      {/* Grid of cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="aspect-video rounded-xl" />
        <Skeleton className="aspect-video rounded-xl" />
        <Skeleton className="aspect-video rounded-xl" />
      </div>

      {/* Main content */}
      <Skeleton className="min-h-[400px] w-full rounded-xl" />
    </div>
  )
}

interface LoadingListProps {
  items?: number
  className?: string
}

/**
 * List skeleton loader
 *
 * @example
 * <LoadingList items={5} />
 */
export function LoadingList({ items = 5, className }: LoadingListProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Inline loading indicator (for buttons, small spaces)
 *
 * @example
 * <Button disabled>
 *   <LoadingInline /> Loading...
 * </Button>
 */
export function LoadingInline({ className }: { className?: string }) {
  return <LoadingSpinner size="sm" className={cn('mr-2', className)} />
}
